"use client";

import Image from "next/image";
import Link from "next/link";
import { BadgeCheck, Ban, Bookmark, Crown, Gavel, Heart, Share2 } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

import { avatarOf, chainName } from "@/features/home-v2/tiles";
import { Avatar } from "@/features/home-v2/ui";
import { formatClock } from "@/lib/countdown";
import { sanitizeUrl } from "@/lib/helpers";
import type { AuctionItem } from "../../../types";
import { formatDateTime, formatPrice, PHASE_LABEL } from "../format";
import type { useAuctionPage } from "../useAuctionPage";

/**
 * THE PIECES EVERY DESIGN IS BUILT FROM — one source for each fact and each
 * action, so the six compositions differ only in where things stand, never in
 * what they say or do. All values come from `useAuctionPage`.
 */

export type Page = ReturnType<typeof useAuctionPage>;
/** `name` is the auction's own title — what its tile in /active-bid shows; `workName` the artwork's, when it differs. */
export type Ctx = { auction: AuctionItem; p: Page; lang: "fa" | "en"; name: string; workName: string | null; workHref: string | null; image: string; description: string };

export const tr = (lang: "fa" | "en") => (fa: string, en: string) => (lang === "fa" ? fa : en);
export const symbolOf = (a: AuctionItem) => (a.currency?.symbol || "").toUpperCase();

/** True after mount — a running clock is read in the browser only (the server's second is not the client's). */
export function useMounted() {
  const [m, setM] = useState(false);
  useEffect(() => setM(true), []);
  return m;
}

/** The phase as a pill. */
export function Phase({ p, lang, className = "" }: { p: Page; lang: "fa" | "en"; className?: string }) {
  return <span className={`ap-phase ${className}`} data-phase={p.phase}><span className="v2-live-dot" aria-hidden />{lang === "fa" ? PHASE_LABEL[p.phase][0] : PHASE_LABEL[p.phase][1]}</span>;
}

/** The clock, small: "2d 04:11:09" while it runs; once it is out, what that means, in words. */
export function Clock({ p, lang, boxes = false }: { p: Page; lang: "fa" | "en"; boxes?: boolean }) {
  const t = tr(lang);
  const nf = new Intl.NumberFormat(lang === "fa" ? "fa-IR" : "en-US", { minimumIntegerDigits: 2 });
  const mounted = useMounted();
  if (!p.countdown) {
    const s = p.phase === "finalising" ? t("نتیجه به‌زودی اعلام می‌شود", "The result is coming soon") : p.phase === "settling" ? t("در حال تسویه", "Settling") : p.phase === "ended" ? t("پایان یافت", "Ended") : p.phase === "canceled" ? t("لغو شد", "Canceled") : "—";
    return <span className="ap-clock ap-clock--out">{s}</span>;
  }
  const c = mounted ? p.countdown : { days: 0, hours: 0, minutes: 0, seconds: 0, totalMs: 0, isZero: false };
  const k = p.phase === "upcoming" ? t("تا شروع", "to start") : t("تا پایان", "left");
  if (boxes) {
    return (
      <span className="ap-boxes" data-phase={p.phase} role="timer" aria-label={k}>
        {[[c.days, t("روز", "d")], [c.hours, t("ساعت", "h")], [c.minutes, t("دقیقه", "m")], [c.seconds, t("ثانیه", "s")]].map(([v, l]) => <span key={String(l)}><b>{nf.format(Number(v))}</b><small>{l}</small></span>)}
      </span>
    );
  }
  return (
    <span className="ap-clock" data-phase={p.phase} role="timer">
      <b>{formatClock(c, lang === "fa")}</b>
      <small>{k}</small>
    </span>
  );
}

/** How much of the auction's own time has passed — a thin bar, start → end. */
export function TimeBar({ p }: { p: Page }) {
  const mounted = useMounted();
  const span = p.startTimeMs !== null && p.expireTimeMs !== null && p.expireTimeMs > p.startTimeMs ? p.expireTimeMs - p.startTimeMs : null;
  const running = p.phase === "live" || p.phase === "ending-soon";
  const share = p.phase === "upcoming" || p.phase === "canceled" ? 0 : running && span ? Math.min(1, Math.max(0, (p.nowMs - (p.startTimeMs as number)) / span)) : 1;
  return <span className="ap-timebar" data-phase={p.phase} aria-hidden><i style={{ inlineSize: `${mounted ? share * 100 : 0}%` }} /></span>;
}

/** When it opens or closes, as a date. */
export function When({ auction, p, lang }: { auction: AuctionItem; p: Page; lang: "fa" | "en" }) {
  const t = tr(lang);
  return <span className="ap-when">{p.phase === "upcoming" ? t("شروع", "Opens") : t("پایان", "Closes")} <b>{formatDateTime(p.phase === "upcoming" ? auction.start_time : auction.expire_time, lang === "fa")}</b></span>;
}

/** The figure that matters now: the highest bid, or the starting price while there is none. */
export function PriceNow({ auction, p, lang, big = true }: { auction: AuctionItem; p: Page; lang: "fa" | "en"; big?: boolean }) {
  const t = tr(lang);
  // The start price, as every auction card shows it — `min_price` is the low end of the bid range, not the start.
  // Once a winner is named, the price is the winning bid.
  const won = p.winnerBid ? Number(p.winnerBid.price) || 0 : 0;
  const current = won || p.highestBid || auction.start_price || auction.min_price;
  return (
    <div className={big ? "ap-now ap-now--big" : "ap-now"}>
      <span className="ap-k">{won ? t("پیشنهاد برنده", "Winning bid") : p.highestBid ? t("بالاترین پیشنهاد", "Highest bid") : t("قیمت پایه", "Starting price")}</span>
      <p>
        {auction.currency?.image ? <Image className="ap-coin" src={sanitizeUrl(auction.currency.image)} alt="" width={26} height={26} /> : null}
        <bdi>{formatPrice(current, lang === "fa")}</bdi><small>{symbolOf(auction)}</small>
      </p>
    </div>
  );
}

/** The range a bid must fall in, as a ruled scale with the highest and the visitor's bid marked. */
export function RangeScale({ auction, p, lang }: { auction: AuctionItem; p: Page; lang: "fa" | "en" }) {
  const t = tr(lang);
  const isFa = lang === "fa";
  const min = Number(auction.min_price) || 0;
  const max = Number(auction.max_price) || 0;
  const at = (v: number) => `${Math.min(100, Math.max(0, ((v - min) / (max - min)) * 100))}%`;
  const mine = p.viewerBid ? Number(p.viewerBid.price) : 0;
  const pos = (v: number) => Math.min(100, Math.max(0, ((v - min) / (max - min)) * 100));
  // Your bid IS the highest: one mark says both. Close to it: your label rises a row, so neither hides the other.
  const same = mine > 0 && mine === p.highestBid;
  const near = !same && mine > 0 && p.highestBid > 0 && Math.abs(pos(mine) - pos(p.highestBid)) < 16;
  return (
    <div className="ap-range">
      <span className="ap-k">{t("پیشنهاد مجاز", "A bid must be")}</span>
      {max > min ? (
        <div className="ap-scale" data-crowded={near || undefined} role="img" aria-label={t(`بین ${formatPrice(min, isFa)} و ${formatPrice(max, isFa)} ${symbolOf(auction)}`, `between ${formatPrice(min, isFa)} and ${formatPrice(max, isFa)} ${symbolOf(auction)}`)}>
          <span className="ap-rule" aria-hidden />
          {p.highestBid > 0 && !same ? <span className="ap-mark" style={{ insetInlineStart: at(p.highestBid) }} aria-hidden><em>{t("بالاترین", "Top")}</em><i /></span> : null}
          {mine > 0 ? <span className="ap-mark ap-mark--mine" data-lift={near || undefined} style={{ insetInlineStart: at(mine) }} aria-hidden><em>{same ? t("شما · بالاترین", "You · Top") : t("شما", "You")}</em><i /></span> : null}
        </div>
      ) : null}
      <div className="ap-ends"><span><small>{t("از", "from")}</small><bdi>{formatPrice(min, isFa)}</bdi></span><span><small>{t("تا", "to")}</small><bdi>{formatPrice(max, isFa)}</bdi> {symbolOf(auction)}</span></div>
    </div>
  );
}

/** The one action — absent, not disabled, for the owner or once bidding is closed; the reason in its place. */
export function BidAction({ p, lang, compact = false }: { p: Page; lang: "fa" | "en"; compact?: boolean }) {
  const t = tr(lang);
  // Open for others, but this visitor has used every bid the auction allows: said here, not first in the dialog.
  if (p.canPlaceBid && p.bidLimitReached) {
    const nf = new Intl.NumberFormat(lang === "fa" ? "fa-IR" : "en-US");
    return (
      <div className="ap-limit" role="status">
        <Ban aria-hidden />
        <p><b>{t("سقف پیشنهادهای شما برای این مزایده پر شده است", "You've used every bid this auction allows")}</b>
          <span>
            <em>{t("پیشنهاد مجاز برای هر نفر:", "Bids per person:")} <bdi>{nf.format(p.maxBidsPerUser)}</bdi></em>
            <em>{t("ثبت‌شده‌ی شما:", "Yours:")} <bdi>{nf.format(p.viewerBidCount)}</bdi></em>
          </span></p>
      </div>
    );
  }
  if (p.canPlaceBid) {
    return (
      <button type="button" className={compact ? "v2-btn ap-go v2-focus" : "v2-btn v2-btn--lg v2-btn--block ap-go v2-focus"} onClick={p.openBid}>
        <Gavel aria-hidden />{p.viewerBid ? t("افزایش پیشنهاد", "Raise your bid") : t("ثبت پیشنهاد", "Place a bid")}
      </button>
    );
  }
  const why = p.settlingNow || p.phase === "settling" ? t("برنده انتخاب شده و مزایده در حال تسویه است؛ پیشنهاد تازه پذیرفته نمی‌شود.", "A winner is chosen and the auction is settling — no new bids are taken.")
    : p.isOwner ? t("این مزایده‌ی خودتان است.", "This is your own auction.")
    : p.phase === "ended" ? t("این مزایده پایان یافته است.", "This auction has ended.")
    : p.phase === "finalising" ? t("زمان پیشنهاد تمام شده.", "Bidding has closed.")
    : p.phase === "canceled" ? t("این مزایده لغو شده است.", "This auction was canceled.")
    : t("الان امکان ثبت پیشنهاد نیست.", "Bidding isn't open right now.");
  return <p className="ap-closed" role="status">{why}</p>;
}

/** Bids, the visitor's own, the winner — three small figures. */
export function Figures({ auction, p, lang }: { auction: AuctionItem; p: Page; lang: "fa" | "en" }) {
  const t = tr(lang);
  const isFa = lang === "fa";
  const nf = new Intl.NumberFormat(isFa ? "fa-IR" : "en-US");
  const winner = p.winnerBid?.user?.name || p.winnerBid?.user?.username;
  return (
    <dl className="ap-figs">
      <div><dt>{t("پیشنهادها", "Bids")}</dt><dd>{nf.format(p.bids.length)}</dd></div>
      <div><dt>{t("پیشنهاد شما", "Your bid")}</dt><dd>{p.viewerBid ? <><bdi>{formatPrice(p.viewerBid.price, isFa)}</bdi><small>{symbolOf(auction)}</small></> : "—"}</dd></div>
      <div><dt><Crown aria-hidden />{t("برنده", "Winner")}</dt><dd className="ap-text">{winner || "—"}</dd></div>
    </dl>
  );
}

/** Who offers it — linked to their profile when there is a handle. */
export function Seller({ auction, lang, size = 32 }: { auction: AuctionItem; lang: "fa" | "en"; size?: number }) {
  const t = tr(lang);
  const nameOf = auction.creator?.name || auction.creator?.username || auction.user?.name || auction.user?.username || t("ناشناس", "Unknown");
  const handle = auction.creator?.username || auction.user?.username || "";
  const body = <><Avatar name={nameOf} src={avatarOf(auction.creator ?? auction.user)} size={size} /><span><small>{t("عرضه‌کننده", "Offered by")}</small><b>{nameOf}</b></span></>;
  return handle ? <Link href={`/${lang}/artists/${handle}`} className="ap-seller v2-focus">{body}</Link> : <span className="ap-seller">{body}</span>;
}

/** Kind · chain · accepted automatically. */
export function Meta({ auction, lang }: { auction: AuctionItem; lang: "fa" | "en" }) {
  const t = tr(lang);
  const raw = String(auction.model_data?.network_name || auction.network?.name || "");
  const chain = chainName(raw, lang === "fa");
  return (
    <span className="ap-meta">
      <span className="v2-chip">{auction.model_type === "collection" ? t("کالکشن", "Collection") : "NFT"}</span>
      {chain ? <span className="v2-chip v2-chip--chain" data-chain={raw.toLowerCase()}>{chain}</span> : null}
      {auction.is_automatic_accept ? <span className="v2-chip ap-auto"><BadgeCheck aria-hidden />{t("پذیرش خودکار", "Auto-accept")}</span> : null}
    </span>
  );
}

/** Like, save, share — the app's own toggles. */
export function Acts({ p, lang, name }: { p: Page; lang: "fa" | "en"; name: string }) {
  const t = tr(lang);
  const nf = new Intl.NumberFormat(lang === "fa" ? "fa-IR" : "en-US");
  const share = async () => {
    try {
      if (navigator.share) await navigator.share({ title: name, url: window.location.href });
      else { await navigator.clipboard.writeText(window.location.href); toast.success(t("لینک کپی شد", "Link copied")); }
    } catch { /* cancelled */ }
  };
  return (
    <span className="ap-acts">
      <button type="button" className="ap-act v2-focus" data-on={p.like.liked || undefined} onClick={p.like.toggle} disabled={p.like.isPending} aria-pressed={p.like.liked} aria-label={p.like.liked ? t("برداشتن پسند", "Unlike") : t("پسندیدن", "Like")}><Heart aria-hidden />{p.like.count > 0 ? <b>{nf.format(p.like.count)}</b> : null}</button>
      <button type="button" className="ap-act v2-focus" data-on={p.save.watchlisted || undefined} onClick={p.save.toggle} disabled={p.save.isPending} aria-pressed={p.save.watchlisted} aria-label={p.save.watchlisted ? t("حذف از واچ‌لیست", "Remove from watchlist") : t("افزودن به واچ‌لیست", "Add to watchlist")}><Bookmark aria-hidden /></button>
      <button type="button" className="ap-act v2-focus" onClick={share} aria-label={t("اشتراک‌گذاری", "Share")}><Share2 aria-hidden /></button>
    </span>
  );
}
